/**
 * Admin Create Page - Central hub for creating various content types
 * Provides quick access to create students, teachers, classes, and homework
 */

import React from 'react';
import {
  SimpleGrid,
  VStack,
  HStack,
  Text,
  Icon,
  Box,
  useColorModeValue,
} from '@chakra-ui/react';
import {
  FiUsers,
  FiUser,
  FiBookOpen,
  FiPlus,
  FiFolder,
  FiUserPlus,
  FiBook,
} from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';

import { AdminLayout, Card, Button, StatsCard } from '@grcs/components';

interface CreateOption {
  title: string;
  description: string;
  icon: any;
  color: string;
  path: string;
  stats?: string;
}

export const AdminCreatePageEnhanced: React.FC = () => {
  const navigate = useNavigate();
  const hoverBg = useColorModeValue('gray.50', 'gray.700');

  const createOptions: CreateOption[] = [
    {
      title: 'Add New Student',
      description: 'Create individual student accounts or bulk upload',
      icon: FiUser,
      color: 'blue.500',
      path: '/admin/user/new/student',
      stats: 'Individual or bulk',
    },
    {
      title: 'Add New Teacher',
      description: 'Create teacher accounts with appropriate permissions',
      icon: FiUserPlus,
      color: 'green.500',
      path: '/admin/user/new/teacher',
      stats: 'Full access',
    },
    {
      title: 'Create New Class',
      description: 'Set up new classes for different grade levels',
      icon: FiUsers,
      color: 'purple.500',
      path: '/admin/class/new',
      stats: 'Grade-based',
    },
    {
      title: 'Create Homework',
      description: 'Add new reading comprehension content to library',
      icon: FiBookOpen,
      color: 'orange.500',
      path: '/admin/library/new',
      stats: 'Rich content',
    },
    {
      title: 'Bulk Upload Students',
      description: 'Upload multiple students using Excel/CSV files',
      icon: FiFolder,
      color: 'red.500',
      path: '/admin/student/bulk',
      stats: 'Excel/CSV',
    },
    {
      title: 'Quick Actions',
      description: 'Access other creation tools and utilities',
      icon: FiPlus,
      color: 'teal.500',
      path: '/admin/tools',
      stats: 'Various tools',
    },
  ];

  const handleCreateClick = (path: string) => {
    navigate(path);
  };

  return (
    <AdminLayout
      header={{
        title: 'Create New Content',
        subtitle:
          'Quick access to create students, teachers, classes, and homework',
        breadcrumb: {
          items: [
            { label: 'Admin', href: '/admin' },
            { label: 'Create', isCurrent: true },
          ],
        },
      }}
    >
      <VStack spacing={8} align="stretch">
        {/* Introduction */}
        <Card padding={6}>
          <VStack spacing={4} align="start">
            <HStack spacing={3}>
              <Icon as={FiPlus} boxSize={6} color="blue.500" />
              <Text fontSize="xl" fontWeight="bold">
                Content Creation Hub
              </Text>
            </HStack>
            <Text color="gray.600">
              Choose from the options below to create new content for the GRCS
              system. Each option provides streamlined workflows for different
              types of content creation.
            </Text>
          </VStack>
        </Card>

        {/* Quick Stats */}
        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
          <StatsCard
            title="Students"
            value="Create"
            icon={FiUser}
            trend="Individual or bulk"
          />
          <StatsCard
            title="Teachers"
            value="Add"
            icon={FiUserPlus}
            trend="Full permissions"
          />
          <StatsCard
            title="Classes"
            value="Setup"
            icon={FiUsers}
            trend="Grade-based"
          />
          <StatsCard
            title="Content"
            value="Library"
            icon={FiBook}
            trend="Rich homework"
          />
        </SimpleGrid>

        {/* Create Options Grid */}
        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
          {createOptions.map((option, index) => (
            <Card
              key={index}
              variant="outline"
              cursor="pointer"
              _hover={{
                shadow: 'md',
                transform: 'translateY(-2px)',
                bg: hoverBg,
              }}
              transition="all 0.2s"
              onClick={() => handleCreateClick(option.path)}
              padding={6}
            >
              <VStack spacing={4} align="start" height="100%">
                <HStack justify="space-between" width="100%">
                  <Box
                    p={3}
                    bg={`${option.color.split('.')[0]}.50`}
                    borderRadius="lg"
                    color={option.color}
                  >
                    <Icon as={option.icon} boxSize={6} />
                  </Box>
                  {option.stats && (
                    <Text fontSize="xs" color="gray.500" fontWeight="medium">
                      {option.stats}
                    </Text>
                  )}
                </HStack>

                <VStack spacing={2} align="start" flex="1">
                  <Text fontSize="lg" fontWeight="bold">
                    {option.title}
                  </Text>
                  <Text fontSize="sm" color="gray.600" lineHeight="1.5">
                    {option.description}
                  </Text>
                </VStack>

                <Button
                  variant="secondary"
                  size="sm"
                  width="100%"
                  leftIcon={<FiPlus size={14} />}
                >
                  Create Now
                </Button>
              </VStack>
            </Card>
          ))}
        </SimpleGrid>

        {/* Recent Activity or Tips */}
        <Card padding={6} variant="filled">
          <VStack spacing={4} align="start">
            <HStack spacing={3}>
              <Icon as={FiBook} boxSize={5} color="blue.500" />
              <Text fontSize="lg" fontWeight="semibold">
                Quick Tips
              </Text>
            </HStack>
            <VStack spacing={2} align="start" fontSize="sm" color="gray.600">
              <Text>
                • Use bulk upload for adding multiple students at once
              </Text>
              <Text>
                • Create classes before adding students to organize them
                properly
              </Text>
              <Text>
                • Teachers need to be created before they can be assigned to
                classes
              </Text>
              <Text>
                • Homework content supports rich text, images, and interactive
                questions
              </Text>
            </VStack>
          </VStack>
        </Card>
      </VStack>
    </AdminLayout>
  );
};
